import { configureNumber } from "../../../setup/util/helpers";
import { SAUDI_COUNTRY_CODE } from "../../../setup/util/phoneRuleConfig.helpers";

// The buyer's mobile, typed any way a Saudi number is written — "+966 50 123 4567",
// "00966501234567", "0501234567" or "501234567", Arabic-Indic digits included — and shown
// grouped as it is typed. The form keeps only the national digits ("0501234567" or
// "501234567"), which the shared phone rules validate and turn into "+966501234567".

export interface IParsedSaudiPhone {
  /** National digits, with or without the leading 0; what the form stores. */
  national: string;
  /** The text the field shows. */
  display: string;
}

/** 9 significant digits ("5X XXX XXXX"), or 10 with the leading zero ("05X XXX XXXX"). */
const maxNationalLength = (national: string) =>
  national.startsWith("0") ? 10 : 9;

/** Groups national digits as Saudi mobiles are written: 2-3-4, or 3-3-4 with the zero. */
const groupNational = (national: string) => {
  const head = national.startsWith("0") ? 3 : 2;
  return [
    national.slice(0, head),
    national.slice(head, head + 3),
    national.slice(head + 3),
  ]
    .filter(Boolean)
    .join(" ");
};

export const parseSaudiPhone = (input: string): IParsedSaudiPhone => {
  const text = `${configureNumber(input ?? "")}`.trim();
  let digits = text.replace(/\D/g, "");
  const international = text.startsWith("+") || digits.startsWith("00");

  if (international) {
    if (digits.startsWith("00")) digits = digits.slice(2);
    // Still typing the dial code: "+", "+9", "+96".
    if (SAUDI_COUNTRY_CODE.startsWith(digits) && digits.length < 3)
      return { national: "", display: `+${digits}` };
    // Another country's number stays as typed, and fails validation (Saudi buyers only).
    if (!digits.startsWith(SAUDI_COUNTRY_CODE))
      return {
        national: digits.slice(0, 15),
        display: `+${digits.slice(0, 15)}`,
      };
    digits = digits.slice(SAUDI_COUNTRY_CODE.length);
    // "+966 05…": the zero is not written after the dial code.
    if (digits.startsWith("0")) digits = digits.slice(1);
    const national = digits.slice(0, 9);
    return {
      national,
      display: `+${SAUDI_COUNTRY_CODE}${
        national ? ` ${groupNational(national)}` : ""
      }`,
    };
  }

  // Pasted without "+" but with the dial code: "966501234567".
  if (digits.startsWith(SAUDI_COUNTRY_CODE) && digits.length > 10)
    return parseSaudiPhone(`+${digits}`);

  const national = digits.slice(0, maxNationalLength(digits));
  return { national, display: groupNational(national) };
};

/** The digits and "+" before `index` in `text`: what a caret position means across formatting. */
export const countSignificant = (text: string, index: number) =>
  (text.slice(0, index).match(/[\d+٠-٩]/g) || []).length;

/** The caret index in `text` just after its `count`-th digit or "+". */
export const indexAfterSignificant = (text: string, count: number) => {
  if (count <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (/[\d+]/.test(text[index])) seen += 1;
    if (seen === count) return index + 1;
  }
  return text.length;
};
